import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import { apiClient } from "../services/api";
import { buttonStyle, buttonBackgroundColor } from "../components/Style";
import PageLayout from "../components/PageLayout";
import { Class } from "../types/Class";
import Dropdown from "../components/Dropdown";
import {
  tanstackTableStyle,
  getTanstackThStyle,
  sortHeaderStyle,
  sortArrowStyle,
  filterContainerStyle,
  tanstackTrStyle,
  getTanstackTdStyle,
  clickableCellStyle,
  emptyRowStyle,
} from "../components/Style";

const columnHelper = createColumnHelper<Class>();

function ClassManagementPage() {
  const [classes, setClasses] = useState<Class[]>([]);
  const [classNameOptions, setClassNameOptions] = useState<string[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<string[]>([]);
  const [classRoomOptions, setClassRoomOptions] = useState<string[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const navigate = useNavigate();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetClass, setDeleteTargetClass] = useState<Class | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");

  // API에서 반 목록 가져오기
  const fetchClasses = async () => {
    try {
      const data = await apiClient.getClasses();
      setClasses(data);
      const uniqueNames = Array.from(new Set((data || []).map((c: Class) => c.className).filter(Boolean)));
      setClassNameOptions(uniqueNames as string[]);
    } catch (error) {
      console.error("반 목록 조회 실패:", error);
    }
  };

  const fetchMeta = async () => {
    try {
      const [teachers, classrooms] = await Promise.all([
        apiClient.getClassTeachers(),
        apiClient.getClassClassrooms(),
      ]);
      setTeacherOptions((teachers || []).map((t: any) => t.teacherName).filter(Boolean));
      setClassRoomOptions((classrooms || []).map((c: any) => c.classroomName).filter(Boolean));
    } catch (error) {
      console.error("메타데이터 조회 실패:", error);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchMeta();
  }, []);

  // 클래스의 가장 빠른 시작 시각, 월요일 유무, 반 이름 계산
  const getClassSortKey = (c: Class): [number, boolean, string] => {
    // 월, 화의 시작 시각만 확인
    let mondayTuesdayMinTime = Infinity;
    let hasMonday = false;

    if (c.monday) {
      const timeStr = (c.mondayStartTime as string) || "23:59:59";
      const [h, m, s] = timeStr.split(":").map(Number);
      const timeInSeconds = h * 3600 + m * 60 + s;
      mondayTuesdayMinTime = Math.min(mondayTuesdayMinTime, timeInSeconds);
      hasMonday = true;
    }

    if (c.tuesday) {
      const timeStr = (c.tuesdayStartTime as string) || "23:59:59";
      const [h, m, s] = timeStr.split(":").map(Number);
      const timeInSeconds = h * 3600 + m * 60 + s;
      mondayTuesdayMinTime = Math.min(mondayTuesdayMinTime, timeInSeconds);
    }

    return [mondayTuesdayMinTime, hasMonday, c.className || ""];
  };

  // 기본 정렬: 월/화 시작 시각 → 월요일 유무 → 반 이름 순
  const sortedClasses = useMemo(() => {
    return [...classes].sort((a, b) => {
      const [aTime, aHasMonday, aName] = getClassSortKey(a);
      const [bTime, bHasMonday, bName] = getClassSortKey(b);

      // 1. 월/화 시작 시각 비교 (빠른 순)
      if (aTime !== bTime) {
        return aTime - bTime;
      }

      // 2. 월요일 유무 비교 (월요일 있는 것 우선)
      if (aHasMonday !== bHasMonday) {
        return aHasMonday ? -1 : 1;
      }

      // 3. 반 이름 사전순 (ABC/가나다)
      return aName.localeCompare(bName, "ko-KR");
    });
  }, [classes]);

  const handleDeleteClass = async (id: number | string) => {
    try {
      await apiClient.deleteClass(id);
      fetchClasses();
    } catch (error) {
      console.error("반 삭제 실패:", error);
    }
  };

  // 24시간 형식을 12시간 형식으로 변환
  const formatTo12Hour = (time24: string): string => {
    if (!time24 || time24 === "00:00") return "-";
    const [hour, minute] = time24.split(":").map(Number);
    const isPM = hour >= 12;
    let hour12 = hour % 12;
    if (hour12 === 0) hour12 = 12;
    const period = isPM ? "오후" : "오전";
    return `${period} ${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  };

  const DAYS = [
    { key: "monday", label: "월", startKey: "mondayStartTime", endKey: "mondayEndTime" },
    { key: "tuesday", label: "화", startKey: "tuesdayStartTime", endKey: "tuesdayEndTime" },
    { key: "wednesday", label: "수", startKey: "wednesdayStartTime", endKey: "wednesdayEndTime" },
    { key: "thursday", label: "목", startKey: "thursdayStartTime", endKey: "thursdayEndTime" },
    { key: "friday", label: "금", startKey: "fridayStartTime", endKey: "fridayEndTime" },
    { key: "saturday", label: "토", startKey: "saturdayStartTime", endKey: "saturdayEndTime" },
    { key: "sunday", label: "일", startKey: "sundayStartTime", endKey: "sundayEndTime" },
  ] as const;

  // 테이블 컬럼 정의
  const columns = useMemo(() => [
    columnHelper.accessor("className", {
      header: "반 이름",
      size: 70,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/class/${info.row.original.id}`)}
        >
          {info.getValue()}
        </span>
      ),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("teacher", {
      header: "담당 선생님",
      size: 70,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/class/${info.row.original.id}`)}
        >
          {info.getValue()}
        </span>
      ),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("classRoom", {
      header: "교실",
      size: 70,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/class/${info.row.original.id}`)}
        >
          {info.getValue() || "-"}
        </span>
      ),
      sortingFn: "alphanumeric",
    }),
    ...DAYS.map((day) =>
      columnHelper.display({
        id: day.key,
        header: () => <div style={{ textAlign: "center", fontWeight: 700 }}>{day.label}</div>,
        size: 100,
        cell: (info) => {
          const c = info.row.original;
          const startKey = day.startKey as keyof Class;
          const endKey = day.endKey as keyof Class;
          const dayEnabled = (c[day.key as keyof Class] as boolean) ?? false;
          const startTime = ((c[startKey] as string) || "00:00").substring(0, 5);
          const endTime = ((c[endKey] as string) || "00:00").substring(0, 5);

          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignItems: "center",
                fontSize: 12,
                cursor: "pointer",
                color: dayEnabled ? "#1b3fa0" : "#ccc",
              }}
              onClick={() => navigate(`/class/${c.id}`)}
            >
              {dayEnabled ? (
                <>
                  <span style={{ fontWeight: 600 }}>{formatTo12Hour(startTime)}</span>
                  <span style={{ color: "#999" }}>~</span>
                  <span style={{ fontWeight: 600 }}>{formatTo12Hour(endTime)}</span>
                </>
              ) : (
                <span>-</span>
              )}
            </div>
          );
        },
      })
    ),
  ], [navigate]);

  // Table instance
  const table = useReactTable({
    data: sortedClasses,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const renderDeleteDialog = () => {
    if (!deleteDialogOpen || !deleteTargetClass) return null;
    return (
      <div
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0,0,0,0.4)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "30px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            textAlign: "center",
            maxWidth: "400px",
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: "20px", color: "#333" }}>
            반 삭제
          </h3>
          <p style={{ marginBottom: "20px", color: "#666" }}>
            <strong>{deleteTargetClass.className}</strong>을(를) 정말 삭제하시겠습니까?
          </p>
          <p style={{ marginBottom: "20px", fontSize: "14px", color: "#999" }}>
            확인을 위해 반 이름을 입력해주세요:
          </p>
          <input
            type="text"
            placeholder="반 이름 입력"
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            style={{
              width: "100%",
              padding: "10px",
              marginBottom: "20px",
              border: "1px solid #ccc",
              borderRadius: "5px",
              boxSizing: "border-box",
            }}
          />
          <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
            <button
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeleteTargetClass(null);
                setDeleteConfirmName("");
              }}
              style={{ ...buttonStyle, background: "#ddd", color: "#333" }}
            >
              취소
            </button>
            <button
              onClick={() => {
                if (deleteConfirmName === deleteTargetClass.className) {
                  handleDeleteClass(deleteTargetClass.id || "");
                  setDeleteDialogOpen(false);
                  setDeleteTargetClass(null);
                  setDeleteConfirmName("");
                } else {
                  alert("반 이름이 일치하지 않습니다.");
                }
              }}
              style={{
                ...buttonStyle,
                background: "#ff6b6b",
                color: "white",
              }}
            >
              삭제
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <PageLayout
      title="반 관리"
      actionRows={[
        (
          <button
            key="add-class"
            onClick={() => navigate("/class/new")}
            style={{ ...buttonStyle, ...buttonBackgroundColor.save }}
          >
            + 반 추가
          </button>
        ),
      ]}
    >
      <style>
        {`
          .class-table tbody tr:hover {
            background-color: #f6f8fa;
          }
        `}
      </style>
      <div style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
        <table className="class-table" style={tanstackTableStyle}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  
                  return (
                    <th
                      key={header.id}
                      style={getTanstackThStyle(false, header.getSize(), {})}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {/* Header with sort */}
                          {header.column.getCanSort() ? (
                            <div
                              onClick={header.column.getToggleSortingHandler()}
                              style={sortHeaderStyle}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: <span style={sortArrowStyle}>▲</span>,
                                desc: <span style={sortArrowStyle}>▼</span>,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}
                          
                          {/* Filter row */}
                          <div style={filterContainerStyle}>
                            {columnId === "className" && (
                              <Dropdown
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(value) => header.column.setFilterValue(value || undefined)}
                                options={[
                                  { value: "", label: "전체" },
                                  ...classNameOptions.map((v) => ({ value: v, label: v })),
                                ]}
                              />
                            )}
                            {columnId === "teacher" && (
                              <Dropdown
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(value) => header.column.setFilterValue(value || undefined)}
                                options={[
                                  { value: "", label: "전체" },
                                  ...teacherOptions.map((v) => ({ value: v, label: v })),
                                ]}
                              />
                            )}
                            {columnId === "classRoom" && (
                              <Dropdown
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(value) => header.column.setFilterValue(value || undefined)}
                                options={[
                                  { value: "", label: "전체" },
                                  ...classRoomOptions.map((v) => ({ value: v, label: v })),
                                ]}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={emptyRowStyle}
                >
                  반이 없습니다.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} style={tanstackTrStyle}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={getTanstackTdStyle(false, cell.column.getSize())}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {renderDeleteDialog()}
    </PageLayout>
  );
}

export default ClassManagementPage;
